import apiClient from '../client';
import { validatedGet, validatedPost, validatedPut, validatedDelete } from '../validatedClient';
import type { ListResponse, PaginationParams } from '@/types/api/common';
import { CategoryListResponseSchema, CategoryDetailResponseSchema } from '@/types/api/validatedSchemas';
import { CategoryCreateSchema } from '@/types/api/generated-schemas';

/**
 * Category type information
 */
export interface CategoryType {
  typeId: number;
  type: string; // "Vendor" | "Customer"
}

/**
 * Transform API response to normalize 'types' -> 'typeInfo'
 * API returns 'types' but frontend expects 'typeInfo'
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformCategory = (raw: any): CategoryDetailResponse => ({
  categoryId: raw.categoryId ?? 0,
  category: raw.category ?? '',
  typeInfo: raw.types || raw.typeInfo || [],
  description: raw.description ?? '',
  color: raw.color ?? '',
  isActive: raw.isActive ?? true,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformCategoryListItem = (raw: any): CategoryListItem => ({
  categoryId: raw.categoryId ?? 0,
  category: raw.category ?? '',
  typeInfo: raw.types || raw.typeInfo || [],
  description: raw.description ?? '',
  color: raw.color ?? '',
  isActive: raw.isActive ?? true,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});

/**
 * Category list item (summary view)
 */
export interface CategoryListItem {
  categoryId: number;
  category: string;
  typeInfo: CategoryType[];
  description: string;
  color: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * GET /api/categories response
 */
export type CategoryListResponse = ListResponse<CategoryListItem>;

/**
 * Category detail (full information)
 */
export interface CategoryDetailResponse {
  categoryId: number;
  category: string;
  typeInfo: CategoryType[];
  description: string;
  color: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * POST /api/categories request body
 *
 * NO `isActive` — the second copy of the same trap `src/types/api/category.ts`
 * carried. `PartyCategoryCreateInput` cannot carry it, so declaring it here
 * only lets a broken call type-check. Activation rides `partyCategorySetActive`
 * via `categoryApi.toggleStatus`.
 */
export interface CreateCategoryRequest {
  category: string;
  typeIds: number[];
  description: string;
  color: string;
}

/**
 * PUT /api/categories/:categoryId request body
 */
export type UpdateCategoryRequest = CreateCategoryRequest;

/**
 * Category list query parameters
 */
export interface CategoryListParams extends PaginationParams {
  search?: string;
  typeIds?: string; // Comma-separated IDs: "1,2"
  isActive?: number; // 1=active, 2=inactive, 3=all
  sortBy?: number; // 1=category, 2=type_id, 3=color
  sortOrder?: number; // 0=ASC, 1=DESC
}

/**
 * Get paginated categories
 * ✅ Runtime validated with Zod
 */
export const getCategories = async (params?: CategoryListParams): Promise<CategoryListResponse> => {
  console.log('[Category API] getCategories - params:', params);
  const rawResponse = await validatedGet('/categories', CategoryListResponseSchema, { params });
  // Transform 'types' -> 'typeInfo' for each category item
  const response: CategoryListResponse = {
    data: (rawResponse.data || []).map(transformCategoryListItem),
    pagination: {
      page: rawResponse.pagination.page ?? 1,
      pageSize: rawResponse.pagination.pageSize ?? 10,
      total: rawResponse.pagination.total ?? 0,
      totalPages: rawResponse.pagination.totalPages ?? 0,
    },
  };
  console.log('[Category API] getCategories - response:', response);
  return response;
};

/**
 * Get a single category by ID
 * ✅ Runtime validated with Zod
 */
export const getCategoryById = async (id: number): Promise<CategoryDetailResponse> => {
  console.log('[Category API] getCategoryById - id:', id);
  const rawResponse = await validatedGet(`/categories/${id}`, CategoryDetailResponseSchema);
  // Transform 'types' -> 'typeInfo'
  const response = transformCategory(rawResponse) as CategoryDetailResponse;
  console.log('[Category API] getCategoryById - response:', response);
  return response;
};

/**
 * Create a new category
 * ✅ Runtime validated with Zod (request + response)
 */
export const createCategory = async (category: CreateCategoryRequest): Promise<CategoryDetailResponse> => {
  console.log('[Category API] createCategory - request:', category);
  // Validate request body before sending
  const validatedRequest = CategoryCreateSchema.parse(category);
  const rawResponse = await validatedPost('/categories', validatedRequest, CategoryDetailResponseSchema);
  // Transform 'types' -> 'typeInfo'
  const response = transformCategory(rawResponse) as CategoryDetailResponse;
  console.log('[Category API] createCategory - response:', response);
  return response;
};

/**
 * Update an existing category
 * ✅ Runtime validated with Zod
 */
export const updateCategory = async (
  id: number,
  category: UpdateCategoryRequest
): Promise<CategoryDetailResponse> => {
  console.log('[Category API] updateCategory - id:', id, 'request:', category);
  const rawResponse = await validatedPut(`/categories/${id}`, category, CategoryDetailResponseSchema);
  // Transform 'types' -> 'typeInfo'
  const response = transformCategory(rawResponse) as CategoryDetailResponse;
  console.log('[Category API] updateCategory - response:', response);
  return response;
};

/**
 * Delete a category
 * ✅ Runtime validated with Zod
 */
export const deleteCategory = async (id: number): Promise<void> => {
  console.log('[Category API] deleteCategory - id:', id);
  await validatedDelete(`/categories/${id}`);
  console.log('[Category API] deleteCategory - success');
};

/**
 * Toggle category status (active/inactive)
 * ✅ Runtime validated with Zod
 */
export const toggleCategoryStatus = async (id: number, isActive: boolean): Promise<void> => {
  console.log('[Category API] toggleCategoryStatus - id:', id, 'isActive:', isActive);
  // Backend Go struct uses IsActive (PascalCase) but JSON tag is isActive (camelCase)
  // Send camelCase in JSON body to match the json:"isActive" tag
  const { data } = await apiClient.patch(`/categories/${id}/toggle`, { isActive });
  console.log('[Category API] toggleCategoryStatus - response:', data);
  return data;
};
